import dayjs from "dayjs";
import { Icon } from "@iconify/react";
import { Table } from "@/components/table";
import { useTranslation } from "react-i18next";
import { useInvitations } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useState } from "react";
import SendInvitationModal from "./send-invitation-modal";
import type { CompanyInvitation } from "@/types/user.types";

const InvitationsTable: React.FC = () => {
  const { t } = useTranslation();
  const { data: invitationsData, isLoading } = useInvitations();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const invitations: CompanyInvitation[] = (invitationsData as CompanyInvitation[] | undefined) || [];

  const getStatusBadge = (status: CompanyInvitation['status']) => {
    const statusMap: Record<CompanyInvitation['status'], { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
      PENDING: { label: t("company.invitations.status.pending"), variant: "default" },
      ACCEPTED: { label: t("company.invitations.status.accepted"), variant: "secondary" },
      EXPIRED: { label: t("company.invitations.status.expired"), variant: "outline" },
      CANCELLED: { label: t("company.invitations.status.cancelled"), variant: "outline" },
    };

    const statusInfo = statusMap[status] || { label: status, variant: "outline" as const };
    return (
      <Badge variant={statusInfo.variant}>
        {statusInfo.label}
      </Badge>
    );
  };

  const formattedInvitations = invitations.map((invitation: CompanyInvitation, index: number) => ({
    ...invitation,
    index: index + 1,
    sentDate: invitation.createdAt
      ? dayjs(invitation.createdAt).format("MMM D, YYYY, h:mm A")
      : "",
    expiresAtDisplay: invitation.expiresAt
      ? dayjs(invitation.expiresAt).format("MMM D, YYYY, h:mm A")
      : "",
    statusBadge: getStatusBadge(invitation.status as CompanyInvitation['status']),
    isExpired: invitation.expiresAt ? dayjs(invitation.expiresAt).isBefore(dayjs()) : false,
  }));

  const columns = [
    { key: "email", label: t("company.invitations.columns.email"), width: 250 },
    { key: "statusBadge", label: t("company.invitations.columns.status"), width: 150 },
    { key: "sentDate", label: t("company.invitations.columns.sentDate"), width: 200 },
    { key: "expiresAtDisplay", label: t("company.invitations.columns.expiresAt"), width: 200 },
  ];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end mb-4">
        <Button onClick={() => setIsModalOpen(true)}>
          <Icon icon="ic:round-add" className="mr-2 h-4 w-4" />
          {t("company.invitations.sendNew")}
        </Button>
      </div>

      {invitations.length === 0 ? (
        <div className="text-center py-12">
          <Icon icon="ic:round-mail-outline" className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600 mb-4">{t("company.invitations.empty")}</p>
        </div>
      ) : (
        <Table
          data={formattedInvitations}
          columns={columns}
          title={t("company.invitations.title")}
          exportable
          isLoading={isLoading}
        />
      )}

      <SendInvitationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default InvitationsTable;

